import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../contexts/AuthContext";

const EMPTY = { email: "", password: "", name: "", role: "viewer" };
const ROLES = [
  { v: "admin", l: "Admin" },
  { v: "operator", l: "Production Operator" },
  { v: "shipping", l: "Warehouse / Shipping" },
  { v: "viewer", l: "Viewer (read-only)" },
];

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const fetchAll = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { fetchAll(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (u) => { setForm({ email: u.email, password: "", name: u.name, role: u.role }); setEditing(u.id); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await api.post("/users", form);
      } else {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editing}`, payload);
      }
      toast.success("Saved");
      close();
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    await api.delete(`/users/${id}`);
    toast.success("Deleted");
    fetchAll();
  };

  return (
    <div data-testid="users-page">
      <PageHeader
        eyebrow="Administration"
        title="Users."
        action={<button onClick={openNew} className="mj-btn mj-btn-primary" data-testid="new-user-btn"><Plus size={14}/> New User</button>}
      />
      <div className="p-6 md:p-10 space-y-6">
        {editing && (
          <form onSubmit={submit} className="mj-card grid md:grid-cols-2 gap-4 items-end" data-testid="user-form">
            <div className="md:col-span-2 flex items-center justify-between">
              <h3 className="font-display text-xl font-bold">{editing === "new" ? "New user" : "Edit user"}</h3>
              <button type="button" onClick={close} className="mj-btn mj-btn-ghost"><X size={14}/> Cancel</button>
            </div>
            <div><label className="data-label block mb-2">Email</label><input required type="email" disabled={editing !== "new"} className="mj-input" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} data-testid="user-email-input"/></div>
            <div><label className="data-label block mb-2">Name</label><input required className="mj-input" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="user-name-input"/></div>
            <div>
              <label className="data-label block mb-2">{editing === "new" ? "Password" : "New password (leave blank to keep)"}</label>
              <input type="password" required={editing === "new"} className="mj-input" value={form.password} onChange={(e)=>setForm({...form, password:e.target.value})} data-testid="user-password-input"/>
            </div>
            <div>
              <label className="data-label block mb-2">Role</label>
              <select className="mj-input" value={form.role} onChange={(e)=>setForm({...form, role:e.target.value})} data-testid="user-role-select">
                {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
              </select>
            </div>
            <div className="md:col-span-2"><button type="submit" className="mj-btn mj-btn-primary" data-testid="user-submit-btn">Save</button></div>
          </form>
        )}

        <div className="mj-card">
          <table className="mj-table" data-testid="users-table">
            <thead><tr><th>Email</th><th>Name</th><th>Role</th><th></th></tr></thead>
            <tbody>
              {users.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-zinc-500">No users.</td></tr>}
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="font-mono-id">{u.email}</td>
                  <td>{u.name}</td>
                  <td><span className="pill pill-in_stock" style={{ background: "#eee", color: "#333", borderColor: "#ccc" }}>{u.role}</span></td>
                  <td className="text-right">
                    <button onClick={() => openEdit(u)} className="mj-btn mj-btn-ghost" style={{minHeight: 36, padding: "0 10px"}} data-testid={`edit-user-${u.id}`}><Pencil size={14}/></button>
                    {currentUser?.id !== u.id && (
                      <button onClick={() => remove(u.id)} className="mj-btn mj-btn-ghost" style={{minHeight: 36, padding: "0 10px", color: "var(--mj-red)"}} data-testid={`del-user-${u.id}`}><Trash2 size={14}/></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
